import { useState } from "react";
import { ShieldAlert, ShieldCheck, AlertTriangle, EyeOff, SearchCheck, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAppMode } from "@/context/AppModeContext";
import { speak } from "@/lib/voice";
import { Loader, Bullets, SpeakButton, SectionCard } from "@/components/common";

const RISK = {
  "low": { label: "Looks Low Risk", color: "#16a34a", icon: ShieldCheck },
  "medium": { label: "Be Careful", color: "#f59e0b", icon: AlertTriangle },
  "high": { label: "High Risk", color: "#ef4444", icon: ShieldAlert },
  "likely-scam": { label: "Likely a Scam", color: "#dc2626", icon: ShieldAlert },
};

export default function Safety() {
  const { mode, isSenior } = useAppMode();
  const [text, setText] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);

  const scan = async () => {
    if (!text.trim()) { toast.info("Paste the message you received first."); return; }
    setLoading(true); setRes(null);
    try {
      const { data } = await api.post("/features/scam-scan", { input: text.trim(), mode });
      if (data.error) toast.error("Could not read that clearly. Try again.");
      else { setRes(data); if (isSenior) speak(`${(RISK[data.risk_level]||{}).label || ""}. ${data.summary}`); }
    } catch { toast.error("Could not scan."); }
    finally { setLoading(false); }
  };

  const r = res ? (RISK[res.risk_level] || RISK.medium) : null;

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <div>
        <h1 className={`font-head font-extrabold flex items-center gap-3 ${isSenior ? "text-4xl" : "text-3xl sm:text-4xl"}`}><ShieldAlert className="w-8 h-8 text-primary" />Is this a scam?</h1>
        <p className={`text-muted-foreground mt-2 ${isSenior ? "text-xl" : ""}`}>Paste any suspicious message, email, text or call. We'll spot the warning signs and tell you what's safe to do — we never guarantee something is safe from looks alone.</p>
      </div>

      <div className="rounded-lg border border-border bg-card p-5">
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={isSenior ? 6 : 5} data-testid="scam-input"
          placeholder="Paste the message here…"
          className={`w-full rounded-md border border-input bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-ring resize-none ${isSenior ? "text-xl" : ""}`} />
        <button onClick={scan} disabled={loading} data-testid="scam-scan" className={`mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground font-bold hover:brightness-110 disabled:opacity-50 transition ${isSenior ? "py-4 text-xl" : "py-3"}`}>
          <SearchCheck className={isSenior ? "w-6 h-6" : "w-5 h-5"} /> Check this message
        </button>
      </div>

      {loading && <Loader label="Checking for warning signs…" />}

      {res && r && (
        <div className="space-y-4 animate-rise">
          <div className="rounded-lg border-2 p-5 flex items-center gap-4" style={{ borderColor: r.color, background: `${r.color}18` }} data-testid="scam-result">
            <r.icon className="w-10 h-10 shrink-0" style={{ color: r.color }} />
            <div>
              <div className="font-head font-extrabold text-2xl" style={{ color: r.color }}>{r.label}</div>
              <p className={isSenior ? "text-xl" : ""}>{res.summary}</p>
            </div>
          </div>
          <SpeakButton text={`${r.label}. ${res.summary}. ${res.safe_next_step}`} />

          {res.warning_signs?.length > 0 && <SectionCard title="Warning signs found" icon={AlertTriangle}><Bullets items={res.warning_signs} /></SectionCard>}
          {res.do_not_share?.length > 0 && <SectionCard title="Never share these" icon={EyeOff}><Bullets items={res.do_not_share} /></SectionCard>}
          {res.what_to_verify?.length > 0 && <SectionCard title="How to check safely" icon={SearchCheck}><Bullets items={res.what_to_verify} /></SectionCard>}
          {res.safe_next_step && <SectionCard title="Do this now" icon={LifeBuoy} accent><p className={isSenior ? "text-xl" : "text-base"}>{res.safe_next_step}</p></SectionCard>}
          {res.reassurance && <p className="text-center text-muted-foreground">{res.reassurance}</p>}
        </div>
      )}
    </div>
  );
}
